import '../../css/style.scss';
import {h, render} from 'preact';
import $ from '@modules/jquery-import';
import Modernizr from 'modernizr';
import Modal from '@plugins/modal';
import {intlNewsletterDialog} from '@util/HtmlComponents';
import dataLayer from './data-layer';
import appLogger from '@core/LoggingProvider';
import assign from 'object-assign';
import namespace from './namespace';
import View from '@core/View';
import User from '@modules/User';
import * as Media from '@util/MediaUtils';
import isSiteFeatureEnabled from '@util/fn/isSiteFeatureEnabled';
import {getCookie, setCookie, COOKIES} from '@util/StorageUtils';
import Cookies from 'js-cookie';
import {$SITE_MESSAGE_BAR} from '@config/selectors';
import CartQuickView from '@modules/cart/CartQuickView';
import SearchSuggestions from '../search-suggestions';
import DentalProDialog from '../components/dialogs/DentalProDialog';
import MiniCart from '../mini-cart';
// todo -> make this a conditional import
import newsletterForm from '../modules/account/newsletterForm';
import Header from '@modules/ui/header';
import Footer from '@modules/ui/Footer';
import EliteStatus from '@modules/account/eliteStatus';
import mediaQuery from '@util/media-queries';
import {
    photoGallery,
    jqueryTabs,
    jqueryScrolling,
    jqueryAccordion,
    jqueryToggleQtySelect,
    jqueryTooltip,
    jqueryIsolatedScroll,
    jqueryCustomFields
} from '@modules/lazy-imports';

// configure exception logger to user context
appLogger.configure( {
    user: {
        id: User.getInfo().uid,
        username: User.getUserName()
    }
} );

// Initialize Common page elements
assign( namespace( 'Site' ), {
    _initializers: {},

    // prepend initializer to a specified namespaced component initalizer
    // useful when needing to initalize nested components such as a tabset within an accordion
    // that needs to set panel height
    addInitializer( namespace, fn ) {
        if ( typeof namespace === 'function' ) {
            fn = namespace;
            namespace = 'default';
        }

        this._initializers[namespace] = this._initializers[namespace] || [];
        this._initializers[namespace].push( fn );
    },

    callInitializers( namespace ) {
        namespace = namespace || 'default';

        var initializers = this._initializers[namespace];

        if ( initializers && initializers.length ) {
            $.each( initializers, function ( i, fn ) {
                fn.call( this );
            }.bind( this ) );
        }
    },

    onDOMLoad() {
        this.patches();

        this.serviceWorker.init();

        Header.init();
        this.messageBar.init();
        this.forms();
        this.searchBar.init();
        this.media.init();
        this.commonUI.init();
        this.account.init();
        this.tracking.init();
        Footer.init();

        if ( upi.conf.isEcommerceSite && navigator.cookieEnabled ) {
            this.cart.init();
        }
    },

    onWindowLoad() {
        console.log( '[Page initalized]' );
    },

    serviceWorker: {
        init() {
            if ( !navigator.serviceWorker ) {
                return;
            }

            if ( process.env.NODE_ENV === 'production' ) {
                // wait until page has loaded before init service worker ot improve performance
                window.addEventListener( 'load', function () {
                    navigator.serviceWorker.register( '/service-worker.js' )
                        .then( reg => {
                            const {waiting, installing} = reg;
                            console.log( '[serviceWorker status]', {waiting, installing} );
                        } )
                        .catch( err => console.warn( '[serviceWorker initialization failed]', err ) );
                } );
            }
        }
    },

    account: {
        init() {
            EliteStatus.init();

            // Domestic newsletter form
            newsletterForm.init( '#newsletterForm' );

            // initialize newsletter form for EU only
            $( '#newsletterForm-eu .button' ).on( 'click', ( e ) => {
                const modal = Modal.getInstance();
                const target = e.currentTarget;
                modal.open( {
                    content: intlNewsletterDialog( {
                        modal,
                        formId: target.dataset['hubspotFormid'],
                        portalId: target.dataset['hubspotPortalid']
                    } ),
                    width: 560
                } );
            } );
        }
    },

    cart: {
        async init() {
            // globally handle item views and cart addition confirmations
            new CartQuickView( 'body' );

            const MiniCart = await import(/*webpackChunkName: "~~upi-minicart"*/ '../mini-cart' );
            if ( MiniCart && MiniCart.default ) {
                MiniCart.default.init( isSiteFeatureEnabled( 'MiniCart' ) );
            }
        }
    },

    patches() {
        // patch: windows safari incomplete cssanimation support
        if ( /windows.*safari/i.test( navigator.userAgent ) ) {
            $( 'html' ).toggleClass( 'cssanimations', 'no-cssanimations' );
        }
    },

    searchBar: {
        init() {
            const searchContainer = document.getElementById( 'search-bar-wrapper--header' );

            if ( isSiteFeatureEnabled( 'SearchSuggestions' ) && searchContainer ) {
                SearchSuggestions.init();
            }
        }
    },

    messageBar: {
        init() {
            const self = this;
            const msgs = document.querySelectorAll( $SITE_MESSAGE_BAR );
            Array.from( msgs ).forEach( node => {
                const cookieName = node.dataset.msgName || 'site-msg';
                const hideNumDays = node.dataset.hideNumDays || 1;
                const isPersistent = node.classList.contains( 'notice' ) || !!hideNumDays;
                // cookieData = COOKIES.SITE_NOTICE_BAR; // defaults to upi_notice
                if ( self.isActive( node, cookieName, isPersistent ) ) {
                    self.show( node );
                    const closeBtn = node.querySelector( '[data-action="close"]' );
                    closeBtn.addEventListener( 'click', () => {
                        if ( isPersistent ) {
                            setCookie(
                                {
                                    // namespace message bar cookies
                                    cookie: COOKIES.SITE_NOTICE_BAR,
                                    key: {name: cookieName},
                                    value: 'disabled'
                                },
                                {expires: 60 * 24 * hideNumDays}
                            ); // disable for specified days OR 24 hours
                        }

                        self.hide( node );
                    } );
                }
            } );
        },
        show( el ) {
            el.classList.remove( 'hidden' );
        },
        hide( el ) {
            el.classList.add( 'hidden' );
        },
        isActive( el, name, isPersistent ) {
            // check that the messageBar is on DOM and and a cookie has not been set for message (which means it's been read and closed)
            if ( isPersistent ) {
                var msgPattern = new RegExp( name + '~disabled', 'g' ),
                    upiNoticeCookie = getCookie(
                        COOKIES.SITE_NOTICE_BAR
                    );

                return (
                    typeof el !== 'undefined' && !msgPattern.test( upiNoticeCookie )
                );
            }

            return true;
        }
    },

    forms() {
        // initialize input masks through data-inputmask attr
        // usage: https://github.com/RobinHerbots/Inputmask#via-data-inputmask-attribute
        const maskableInputs = $( ':input[data-inputmask]' );
        upi.forms.inputmask.mask( maskableInputs );

        var $form = $( '#aspnetForm' ), $visibleFields;

        if ( $form[0] ) {
            $visibleFields = $( $form[0].elements ).filter( function ( el ) {
                return 'hidden' != el.type;
            } );

            // bind auto-clear
            $visibleFields.filter( '[data-autoclear]' ).on( 'blur', function () {
                this.value = '';
            } );
        }
    },

    media: {
        init() {

            const handleImage404 = function ( e ) {
                const el = e.target;
                if ( el.nodeName.toLowerCase() === 'img' ) {
                    el.classList.add( 'img-broken' );
                }
            };
            // bind to event capture phase since 'error' events do not bubble
            document.body.addEventListener( 'error', handleImage404, true );

            $( '#body-content a[href*="//www.youtube.com"]' ).on( 'click', function ( e ) {
                var target = e.currentTarget;

                // ignore direct links to the ultradent youtube channel
                if ( target.href && target.href.match( /\/ultradentproducts|\/internationalupi|playlist/i ) ) {
                    return true;
                }

                e.preventDefault();
                if ( Modernizr.mq( mediaQuery.max['screenMedium'] ) ) {
                    // embed video to target
                    Media.embedMediaToTarget.call( target, true );
                }
                else {
                    Modal.getInstance().open( {
                        width: '75%',
                        content: Media.getMediaEmbedFor( target.href, true )
                    } );
                }
            } );
        }
    },

    commonUI: {
        init() {
            var $mainContent = $( '#body-content' );

            upi.Site.callInitializers( 'commonUI' );

            if ( !navigator.cookieEnabled ) {
                $( 'html' ).addClass( 'no-cookies' );

                $( '#loginForm1' ).replaceWith( `
                    <a href="/login" class="button w-full my-4">
                        <svg class="svgi-lock-closed text-xl mr-2">
                            <use xlink:href="/svg/ui-defs.svg#icon-lock-closed"></use>
                        </svg>
                        Sign in
                    </a>
                ` );
            }

            if ( !upi.env.IS_ENUS ) {
                $( 'html' ).addClass( 'international' );
            }

            if ( window.parent !== window ) {
                $( 'html' ).addClass( 'is-iframe' );
            }

            if ( upi.conf.isDentalProRequired && !Cookies.get( 'UpiDentalProfessional' ) ) {
                render(
                    <DentalProDialog/>,
                    document.getElementById( 'body-content' ) );
            }

            // declarative hook for disabling touch events on links
            $( document )
                .on( 'click', 'a[data-prevent-touch-nav]', e => window.USER_IS_TOUCHING && e.preventDefault() );

            // initialize print buttons
            $( document ).on( 'click', '.button.print', function ( e ) {
                e.preventDefault();
                window.print();
            } );

            // note -> since Modernizr's touchevents check only detects whether browser is capable of touchevents
            //  we need to apply our own check to determine if the user is actually using their touch screen
            $( 'html' ).addClass( 'no-screentouches' );

            // detect if user is using touch interface on touch/hybrid device
            $( window ).on( 'touchstart', function onFirstTouch() {
                window.USER_IS_TOUCHING = true;
                $( 'html' ).removeClass( 'no-screentouches' ).addClass( 'screentouches' );
                $( window ).off( 'touchstart', onFirstTouch );
            } );

            // simple collapsing content -> uses .collapse-group component
            $( document ).on( 'click, touchstart', '[data-collapse-trigger]', e => {
                const el = e.currentTarget;

                if ( !el.dataset.disabled ) {
                    e.preventDefault();
                    $( el ).parents( '.collapse-group' ).toggleClass( 'collapse-group--active' );
                }
            } );

            // restrict form input (with class"digits") to digits only
            $( document ).on(
                'keypress',
                '[contenteditable], input, textarea',
                upi.forms.restrict_input_values
            );

            // trim all input values
            $( document ).on( 'change', 'input, textarea', upi.forms.handleTrimInput );

            const logPluginWarning = this.logPluginWarning;

            this.loadPluginIfNodeExists( photoGallery, '[data-photo-gallery]' )
                .catch( logPluginWarning );

            this.loadPluginIfNodeExists( jqueryScrolling, 'a[data-auto-scroll]' )
                .then( () => {
                    // bind auto-scrollers
                    $( document ).on( 'click', 'a[data-auto-scroll]', function ( e ) {
                        e.preventDefault();
                        var $target = $( '#' + this.href.split( '#' )[1] );
                        // only attach to <a href="#target">
                        if ( !this.href || !$target[0] ) {
                            return;
                        }
                        $( $target ).scrollToElement();
                    } );
                } )
                .catch( logPluginWarning );

            this.loadPluginIfNodeExists( jqueryTabs, '.tabset' )
                .then( nodes => nodes.tabs() )
                .catch( logPluginWarning );

            this.loadPluginIfNodeExists( jqueryAccordion, '.accordion' )
                .then( nodes => {
                    nodes
                        .filter( function () {
                            return this.parentNode.nodeName.toLowerCase() !== 'code';
                        } )
                        .accordion( {single_view: true} );
                } )
                .catch( logPluginWarning );

            // general tooltips
            this.loadPluginIfNodeExists( jqueryTooltip, document )
                .then( nodes => nodes.tooltip() )
                .catch( logPluginWarning );

            this.loadPluginIfNodeExists( jqueryIsolatedScroll, '.navmenu' )
                .then( nodes => nodes.isolatedScroll() )
                .catch( logPluginWarning );

            // convert SKU qty selector to 1-10+ dropdown that converts to an input when 10+ is selected
            this.loadPluginIfNodeExists( jqueryToggleQtySelect, 'input[data-qty-select-toggle]' )
                .then( nodes => nodes.toggleQtySelect() )
                .catch( logPluginWarning );

            // convert fields to custom that fall within the main content wrapepr and hidden light boxes
            jqueryCustomFields().then( () => {
                $( '#body-content, .inline-lightbox' )
                    .find( 'input[type="file"], :radio, :checkbox' )
                    .toCustomFields();
            } );

            // insert backorder policy dialog if not already on page
            if ( !$( '#backorderPolicy' ).length ) {
                let backorderView = new View( 'backorderPolicy' );
                setTimeout( function () {
                    $( 'body' ).append( backorderView.render( {} ) );
                }, 500 );
            }
        },
        loadPluginIfNodeExists: ( pluginPromise, selector ) => {
            const node = $( selector );
            if ( node.length > 0 ) {
                return pluginPromise().then( () => node );
            }

            return Promise.reject( `[Deferred plugin] "${pluginPromise.name}" because '${selector}' not found` );
        },
        logPluginWarning: err => console.warn( err )
    },

    tracking: {
        init() {
            // dataLayer.promotions
            this.promotionTracking();
            this.pageViews();
        },

        getPromoData( el ) {
            if ( !el ) {
                console.error( `expected "el" to be HTMLElement and received ${el} instead` );
            }
            const {promoId, promoName, promoCreativeName, promoPosition} = el.dataset;
            return {promoId, promoName, promoCreativeName, promoPosition};
        },

        promotionTracking() {
            // track promotion clicks for elements with [data-promo-id]
            $( '[data-promo-id]' ).on( 'click', this.handlePromoClick.bind( this ) );

            // track when promotions are in viewport for elements with [data-promo-id]
            const promoObserver = new IntersectionObserver( entries => {
                for ( let i = 0; i < entries.length; i++ ) {
                    if ( entries[i].isIntersecting ) {
                        const el = entries[i].target;
                        this.handlePromoView( el );
                        promoObserver.unobserve( el );
                    }
                }
            }, {threshold: [0.5]} );

            const promoItems = document.querySelectorAll( '[data-promo-id]' );
            if ( promoItems ) {
                Array.from( promoItems ).forEach( promo => promoObserver.observe( promo ) );
            }
        },

        handlePromoClick( e ) {
            e.preventDefault();
            const el = e.currentTarget;
            const promoData = this.getPromoData( el );
            const navigate = () => window.location = el.href;
            dataLayer.promotions.sendPromoClick( promoData, navigate );
        },

        handlePromoView( el ) {
            const promoData = this.getPromoData( el );
            dataLayer.promotions.sendPromoView( promoData );
        },

        pageViews() {
            window.addEventListener( 'load', function () {
                switch ( window.location.pathname ) {
                    case '/':
                        return dataLayer.pageView.homepage();

                    case '/checkout':
                        return dataLayer.pageView.cart();

                    case '/checkout/confirmation':
                        const $orderTotal = $( '.order-total > .value' );
                        const purchaseTotal = $orderTotal ? $orderTotal.text() : 0;
                        return dataLayer.pageView.purchaseComplete( {
                            value: purchaseTotal,
                            currency: 'USD'
                        } );
                }
            } );
        }
    }
} );

upi.Site.onDOMLoad();

$( window ).on( 'load', function () {
    upi.Site.onWindowLoad();
    window.scrollBy && window.scrollBy( 1, 0 );
} );
